import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Eye, Smartphone, AlertTriangle, CheckCircle, XCircle, RefreshCw, Shield, Upload } from 'lucide-react';
import { Card, Badge, Button } from '@azyntor-labs/nexus-ui';

interface Detection {
  type: 'person' | 'object' | 'violation';
  label: string;
  confidence: number;
  timestamp: Date;
  flagged: boolean;
  screenshotUrl?: string;
}

const PROHIBITED = ['cell phone', 'book', 'laptop', 'remote', 'scissors', 'knife'];

export const AITestPage = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [personCount, setPersonCount] = useState(0);
  const [objectsFound, setObjectsFound] = useState<string[]>([]);
  const [allObjects, setAllObjects] = useState<string[]>([]);
  const [violations, setViolations] = useState<Detection[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const objectModelRef = useRef<any>(null);
  const lastViolationTimeRef = useRef<number>(0);

  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
    } catch {
      setError('Camera access denied. Use Chrome with --unsafely-treat-insecure-origin-as-secure flag for this domain.');
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    stopDetection();
  };

  const loadModels = async () => {
    setError(null);
    setModelLoading(true);
    try {
      await import('@tensorflow/tfjs');
      const cocoSsd = await import('@tensorflow-models/coco-ssd');
      const model = await cocoSsd.load();
      objectModelRef.current = model;
      setModelLoaded(true);
    } catch (err: any) {
      setError(`Failed to load AI model: ${err.message}`);
    } finally {
      setModelLoading(false);
    }
  };

  // Capture screenshot from video
  const captureScreenshot = useCallback((): string | null => {
    if (!videoRef.current || videoRef.current.readyState < 2) return null;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(videoRef.current, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.8);
  }, []);

  const runDetection = useCallback(async () => {
    if (!videoRef.current || videoRef.current.readyState < 2 || !objectModelRef.current) return;

    try {
      const predictions = await objectModelRef.current.detect(videoRef.current);
      const newDetections: Detection[] = [];
      const newViolations: Detection[] = [];
      const now = Date.now();

      // Count persons
      const persons = predictions.filter((p: any) => p.class === 'person' && p.score > 0.5);
      setPersonCount(persons.length);

      // Multiple persons = violation
      if (persons.length > 1) {
        const v: Detection = {
          type: 'violation', label: `${persons.length} people detected — only 1 person allowed`,
          confidence: 0.9, timestamp: new Date(), flagged: true,
        };
        newDetections.push(v);
        newViolations.push(v);
      } else if (persons.length === 0) {
        const v: Detection = {
          type: 'violation', label: 'No person detected — student may have left',
          confidence: 1, timestamp: new Date(), flagged: true,
        };
        newDetections.push(v);
        newViolations.push(v);
      } else {
        newDetections.push({
          type: 'person', label: `Person detected (${(persons[0].score * 100).toFixed(0)}%)`,
          confidence: persons[0].score, timestamp: new Date(), flagged: false,
        });
      }

      // Prohibited objects
      const prohibited = predictions.filter((p: any) => PROHIBITED.includes(p.class) && p.score > 0.4);
      const foundNames = prohibited.map((p: any) => `${p.class} (${(p.score * 100).toFixed(0)}%)`);
      setObjectsFound(foundNames);

      prohibited.forEach((p: any) => {
        const v: Detection = {
          type: 'violation', label: `Prohibited: ${p.class} (${(p.score * 100).toFixed(0)}%)`,
          confidence: p.score, timestamp: new Date(), flagged: true,
        };
        newDetections.push(v);
        newViolations.push(v);
      });

      // Auto-capture screenshot on violation (throttled to once per 5 seconds)
      if (newViolations.length > 0 && now - lastViolationTimeRef.current > 5000) {
        lastViolationTimeRef.current = now;
        const screenshot = captureScreenshot();
        if (screenshot) {
          setScreenshots(prev => [screenshot, ...prev].slice(0, 20));
          newViolations.forEach(v => v.screenshotUrl = screenshot);
        }
      }

      if (newViolations.length > 0) {
        setViolations(prev => [...newViolations, ...prev].slice(0, 50));
      }

      // All detected objects
      const all = predictions.filter((p: any) => p.score > 0.4).map((p: any) => `${p.class} (${(p.score * 100).toFixed(0)}%)`);
      setAllObjects(all);

      // Draw bounding boxes
      if (canvasRef.current && videoRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          const vw = videoRef.current.videoWidth;
          const vh = videoRef.current.videoHeight;
          canvasRef.current.width = vw;
          canvasRef.current.height = vh;
          ctx.clearRect(0, 0, vw, vh);

          predictions.forEach((pred: any) => {
            if (pred.score > 0.4) {
              const [x, y, w, h] = pred.bbox;
              const isProhibited = PROHIBITED.includes(pred.class);
              const isPerson = pred.class === 'person';
              const isMultiplePeople = isPerson && persons.length > 1;

              ctx.strokeStyle = isProhibited ? '#ef4444' : isMultiplePeople ? '#f59e0b' : isPerson ? '#14b8a6' : '#6b7280';
              ctx.lineWidth = isProhibited || isMultiplePeople ? 3 : 2;
              ctx.strokeRect(x, y, w, h);

              const label = `${pred.class} ${(pred.score * 100).toFixed(0)}%`;
              ctx.font = 'bold 13px Inter, sans-serif';
              const tw = ctx.measureText(label).width;
              ctx.fillStyle = isProhibited ? '#ef4444' : isMultiplePeople ? '#f59e0b' : isPerson ? '#14b8a6' : '#6b7280';
              ctx.fillRect(x, y - 20, tw + 8, 20);
              ctx.fillStyle = '#fff';
              ctx.fillText(label, x + 4, y - 5);
            }
          });
        }
      }

      if (newDetections.length > 0) {
        setDetections(prev => [...newDetections, ...prev].slice(0, 100));
      }
    } catch (err) {
      console.error('Detection error:', err);
    }
  }, [captureScreenshot]);

  const startDetection = () => {
    if (!cameraActive || !modelLoaded) return;
    setIsDetecting(true);
    runDetection();
    detectionIntervalRef.current = setInterval(runDetection, 1500);
  };

  const stopDetection = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    setIsDetecting(false);
  };

  useEffect(() => {
    return () => { stopCamera(); };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">AI Proctoring Test</h1>
        <p className="text-white/50 mt-1">Test person detection, prohibited object detection, and violation screenshots</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Violation Alert Banner */}
      {violations.length > 0 && isDetecting && (
        <div className="bg-red-500/15 border border-red-500/40 rounded-lg p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-400" />
            <div>
              <p className="text-red-400 font-semibold">{violations.length} Violation(s) Detected</p>
              <p className="text-red-400/70 text-sm">{violations[0]?.label}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Camera + Canvas */}
        <div className="lg:col-span-2">
          <Card>
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Camera className="w-5 h-5 text-teal-400" />
                  Camera Feed
                  {isDetecting && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
                </h2>
                <div className="flex gap-2">
                  {!cameraActive ? (
                    <Button size="sm" onClick={startCamera}>Start Camera</Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={stopCamera}>Stop Camera</Button>
                  )}
                </div>
              </div>

              <div className="relative aspect-video bg-black/50 rounded-lg overflow-hidden">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
                <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ transform: 'scaleX(-1)' }} />
                {!cameraActive && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-white/50">Click "Start Camera" to begin</p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" onClick={loadModels} disabled={modelLoaded || modelLoading}>
                  {modelLoading ? 'Loading...' : modelLoaded ? 'Model Ready' : 'Load AI Model'}
                </Button>
                {!isDetecting ? (
                  <Button size="sm" onClick={startDetection} disabled={!cameraActive || !modelLoaded}>
                    <Eye className="w-4 h-4 mr-1" /> Start Detection
                  </Button>
                ) : (
                  <Button size="sm" variant="secondary" onClick={stopDetection}>Stop Detection</Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => { setDetections([]); setViolations([]); setScreenshots([]); setPersonCount(0); setObjectsFound([]); setAllObjects([]); }}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Clear All
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Status Panel */}
        <div className="space-y-4">
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wide mb-3">Status</h3>
              <div className="space-y-2">
                {[
                  ['Camera', cameraActive, cameraActive ? 'Active' : 'Off'],
                  ['AI Model', modelLoaded, modelLoaded ? 'Ready' : modelLoading ? 'Loading...' : 'Not Loaded'],
                  ['Detection', isDetecting, isDetecting ? 'Running' : 'Stopped'],
                ].map(([label, ok, text]) => (
                  <div key={label as string} className="flex items-center justify-between">
                    <span className="text-sm text-white/60">{label as string}</span>
                    <Badge variant={ok ? 'success' : 'warning'} size="sm">{text as string}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wide mb-3">Live Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {personCount === 1 ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
                  <div>
                    <p className="text-sm text-white font-medium">Persons: {personCount}</p>
                    <p className="text-xs text-white/50">
                      {personCount === 0 ? 'No person visible' : personCount === 1 ? 'OK' : `${personCount} people — VIOLATION`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {objectsFound.length === 0 ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <Smartphone className="w-5 h-5 text-red-400" />}
                  <div>
                    <p className="text-sm text-white font-medium">Prohibited: {objectsFound.length}</p>
                    {objectsFound.length > 0 && <p className="text-xs text-red-400">{objectsFound.join(', ')}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 text-cyan-400" />
                  <div>
                    <p className="text-sm text-white font-medium">Screenshots: {screenshots.length}</p>
                    <p className="text-xs text-white/50">Auto-captured on violations</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wide mb-3">All Objects</h3>
              <div className="flex flex-wrap gap-1.5">
                {allObjects.length === 0 ? (
                  <p className="text-xs text-white/40">None</p>
                ) : (
                  allObjects.map((obj, i) => (
                    <Badge key={i} variant={PROHIBITED.some(p => obj.startsWith(p)) ? 'danger' : obj.startsWith('person') ? 'success' : 'info'} size="sm">{obj}</Badge>
                  ))
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Violation Screenshots */}
      {screenshots.length > 0 && (
        <Card>
          <div className="p-4">
            <h3 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Violation Screenshots ({screenshots.length})
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {screenshots.map((src, i) => (
                <img key={i} src={src} alt={`Violation ${i + 1}`} className="rounded-lg border border-red-500/30 w-full aspect-video object-cover" />
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Detection Log */}
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wide mb-3">Detection Log ({detections.length})</h3>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {detections.length === 0 ? (
              <p className="text-sm text-white/40 text-center py-4">No detections yet.</p>
            ) : (
              detections.map((d, i) => (
                <div key={i} className={`flex items-center gap-2 text-xs py-1 border-b border-white/[0.04] ${d.flagged ? 'bg-red-500/5' : ''}`}>
                  <span className="text-white/40 w-16 shrink-0">{d.timestamp.toLocaleTimeString()}</span>
                  <Badge variant={d.flagged ? 'danger' : d.type === 'person' ? 'success' : 'info'} size="sm">
                    {d.flagged ? 'VIOLATION' : d.type}
                  </Badge>
                  <span className={d.flagged ? 'text-red-400' : 'text-white/70'}>{d.label}</span>
                  {d.screenshotUrl && <Badge variant="warning" size="sm">Screenshot</Badge>}
                </div>
              ))
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
